import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { offerConverter, offerDetailsConverter } from '@seu-converter';
import { SeuFormValue, SeuSelectColorOption, SeuSelectOption } from '@seu-form';
import { SEU_NEW_ID } from '@seu-helpers';
import { RequestService, StoreService, ToastService } from '@seu-services';
import {
  SEU_API_ENDPOINTS,
  SeuCreateOfferDetails,
  SeuInterconnection,
  SeuOffer,
  SeuOfferApi,
} from '@seu-types';
import { orderBy } from 'lodash-es';
import { catchError, firstValueFrom, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  constructor(
    private store: StoreService,
    private requestService: RequestService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  public async getOfferById(id: string): Promise<SeuOffer | undefined> {
    const endpoint = SEU_API_ENDPOINTS.offerDetail.replace(':offerId', id);

    const offerApi = await firstValueFrom(
      this.requestService.get<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS)
    );

    const offer = offerConverter.fromApi(offerApi);

    if (!offer) {
      return;
    }

    return offer;
  }

  public async updateOffer(
    offer: SeuOffer
  ): Promise<{ offer: SeuOffer | null; status: number }> {
    const bodyData = offerDetailsConverter.toApi(offer);
    const endpoint = SEU_API_ENDPOINTS.offerDetail.replace(
      ':offerId',
      offer.id
    );

    let status = 200;

    const responseData = await firstValueFrom(
      this.requestService
        .put<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, bodyData)
        .pipe(
          catchError((e) => {
            const error = e as { status: number };
            status = error.status;
            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      return { offer: updatedOffer, status };
    }

    return { offer: null, status };
  }

  public getCalculationSelectOptionsGlobalParameters(
    offer: SeuOffer
  ): SeuSelectOption<SeuFormValue>[] {
    const defaultId = offer.globalParameters.find(
      (globalParameter) => globalParameter.isDefault
    )?.id;

    return offer.globalParameters
      .filter((globalParameter) => globalParameter.id !== SEU_NEW_ID)
      .map((globalParameter) => {
        return {
          key: globalParameter.id,
          label:
            globalParameter.title +
            (globalParameter.id === defaultId
              ? ` <span class='text-gray-500'>(${this.translateService.instant('global.default')})</span>`
              : ''),
        };
      });
  }

  public getCalculationSelectOptionsInterconnections(
    offer: SeuOffer,
    activeInterconnectionId: string | undefined
  ): SeuSelectOption<SeuFormValue>[] {
    return offer.interconnections.map((interconnection) => {
      return {
        key: interconnection.id,
        label:
          interconnection.name +
          (interconnection.id === activeInterconnectionId
            ? ` <span class='text-gray-500'>(${this.translateService.instant('global.active')})</span>`
            : ''),
      };
    });
  }

  public getCalculationSelectOptionsOperatingConditions(
    activeInterconnection: SeuInterconnection
  ): SeuSelectColorOption<SeuFormValue>[] {
    const activeOperatingConditionId =
      activeInterconnection.operatingConditions.find(
        (operatingCondition) => operatingCondition.active
      )?.id;

    return activeInterconnection.operatingConditions
      .filter((operatingCondition) => operatingCondition.id !== SEU_NEW_ID)
      .map((operatingCondition) => {
        return {
          key: operatingCondition.id,
          label: operatingCondition.title,
          sublabel:
            activeOperatingConditionId === operatingCondition.id
              ? 'operating-condition.active'
              : undefined,
          color: 'blue',
          filterTags: operatingCondition.categories ?? [],
        };
      });
  }

  public async getAllOffers(): Promise<SeuOffer[] | undefined> {
    const offersApi = await firstValueFrom(
      this.requestService.get<SeuOfferApi[]>(SEU_API_ENDPOINTS.offers)
    );

    const offers = offersApi.map((offer) => offerConverter.fromApi(offer));

    return orderBy(offers, (offer) => offer.createdAt, 'desc');
  }

  public async createNewOffer(
    data: SeuCreateOfferDetails
  ): Promise<{ offer: SeuOffer | null; status: number }> {
    let offerApi: SeuOfferApi | null = null;
    let offer: SeuOffer | null = null;
    let status = 200;
    try {
      offerApi = await firstValueFrom(
        this.requestService.post<SeuOfferApi>(SEU_API_ENDPOINTS.offers, data)
      );
    } catch (e) {
      const error = e as { status: number };
      status = error.status;
    } finally {
      if (offerApi) {
        offer = offerConverter.fromApi(offerApi);
        // this.toastService.addToast({
        //   type: 'success',
        //   text: 'offers.toasts.create-success',
        // });
      }
    }
    if (status !== 200) {
      offer = null;
    }
    return { offer, status };
  }

  public async archiveOffer(offerId: string): Promise<unknown | null> {
    const endpoint = SEU_API_ENDPOINTS.archiveOffer.replace(
      ':offerId',
      offerId
    );
    const result = await firstValueFrom(
      this.requestService
        .put<unknown>(endpoint as SEU_API_ENDPOINTS, null)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'offer-detail.toasts.delete-error',
            });

            return of(null);
          })
        )
    );

    if (result) {
      return result;
    }

    return null;
  }
}
